import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Maximize2, Minimize2, Monitor, AlertCircle, Power, Clipboard } from 'lucide-react';
import { getCookie } from '../utils/cookies';
import { getSocketUrl, sessionsApi } from '../services/api';
import { getTenant } from '../services/tenant';
import { Button, Chip, IconButton, OutcomeBadge, RecordingIndicator, Spinner } from '@/components/ds-zta';
import { canvasBackground, guacColor } from '@/lib/terminalTheme';
import { splitSensitiveParams, takeRdpPassword } from '@/lib/rdpHandoff';
import { copyText } from '@/utils/clipboard';
import { isReleaseCombo, RELEASE_HINT } from '@/lib/remoteKeyboard';
import { toast } from '@/lib/toast';

/**
 * DesktopSession — Browser-based RDP via custom Guacamole protocol renderer.
 *
 * Implements the Guacamole protocol display instructions with proper:
 * - Channel mask compositing (copy, source-over, source-atop, etc.)
 * - img/blob/end stream-based image rendering with ack flow control
 * - copy region-to-region transfers between layers
 * - rect/cfill/close path-based fills
 * - cursor instruction for custom cursor rendering
 * - clipboard bidirectional support (copy/paste)
 * - sync frame acknowledgment
 * - Layer management (size, move, dispose)
 */

type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error';

// Stream data for img/blob/end and clipboard
interface StreamData {
    type: 'image' | 'clipboard';
    // Image fields
    layer?: number;
    mode?: number;
    mimetype: string;
    x?: number;
    y?: number;
    data: string;
}

// Layer state
interface LayerState {
    canvas: HTMLCanvasElement;
    x: number;
    y: number;
    parent: number;
}

// Map Guacamole channel mask → Canvas composite operation
// From guacamole-common-js Layer.js compositeOperation mapping
const COMPOSITE_OPS: Record<number, GlobalCompositeOperation> = {
    0x1: 'destination-in',
    0x2: 'destination-out',
    0x4: 'source-in',
    0x6: 'source-atop',
    0x8: 'source-out',
    0x9: 'destination-atop',
    0xA: 'xor',
    0xB: 'destination-over',
    0xC: 'copy',
    0xE: 'source-over',
    0xF: 'lighter',
};

function getCompositeOp(mask: number): GlobalCompositeOperation {
    return COMPOSITE_OPS[mask] || 'source-over';
}

// Encode a Guacamole protocol instruction
function encodeInstruction(opcode: string, ...args: string[]): string {
    const parts = [`${opcode.length}.${opcode}`, ...args.map(a => `${a.length}.${a}`)];
    return parts.join(',') + ';';
}

// Convert keyboard events to X11 keysyms
function keyToKeysym(e: KeyboardEvent): number | null {
    const keyMap: Record<string, number> = {
        'Backspace': 0xFF08, 'Tab': 0xFF09, 'Enter': 0xFF0D, 'Escape': 0xFF1B,
        'Delete': 0xFFFF, 'Home': 0xFF50, 'End': 0xFF57, 'PageUp': 0xFF55,
        'PageDown': 0xFF56, 'ArrowLeft': 0xFF51, 'ArrowUp': 0xFF52,
        'ArrowRight': 0xFF53, 'ArrowDown': 0xFF54, 'Insert': 0xFF63,
        'F1': 0xFFBE, 'F2': 0xFFBF, 'F3': 0xFFC0, 'F4': 0xFFC1,
        'F5': 0xFFC2, 'F6': 0xFFC3, 'F7': 0xFFC4, 'F8': 0xFFC5,
        'F9': 0xFFC6, 'F10': 0xFFC7, 'F11': 0xFFC8, 'F12': 0xFFC9,
        'Shift': 0xFFE1, 'Control': 0xFFE3, 'Alt': 0xFFE9, 'Meta': 0xFFEB,
        'CapsLock': 0xFFE5, 'NumLock': 0xFF7F, 'ScrollLock': 0xFF14,
        ' ': 0x0020,
    };
    if (keyMap[e.key]) return keyMap[e.key];
    if (e.key.length === 1) return e.key.charCodeAt(0);
    return null;
}

export default function DesktopSession() {
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const releaseTargetRef = useRef<HTMLButtonElement>(null);
    const [canvasFocused, setCanvasFocused] = useState(false);
    // RDP credentials, resolved once per mount before the socket opens (F11).
    const [creds, setCreds] = useState<{ username: string; password: string } | null>(null);
    const credsPromiseRef = useRef<Promise<{ username: string; password: string }> | null>(null);
    const wsRef = useRef<WebSocket | null>(null);
    const [state, setState] = useState<ConnectionState>('connecting');
    const [error, setError] = useState<string>('');
    const [isFullscreen, setIsFullscreen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const displayContainerRef = useRef<HTMLDivElement>(null);
    const [resourceName] = useState(id || '');
    const layersRef = useRef<Map<number, LayerState>>(new Map());
    const streamsRef = useRef<Map<string, StreamData>>(new Map());
    const cursorCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const cursorHotspotRef = useRef({ x: 0, y: 0 });
    const clipboardTextRef = useRef<string>('');
    const nextStreamIdRef = useRef(1);
    const [clipboardNotice, setClipboardNotice] = useState('');

    // Browser recording: a MediaRecorder capture of this canvas, uploaded when
    // the socket closes. SECONDARY, not evidence (design eval F20, D2 = A): the
    // server's own record of the session (the JSONL events the gateway writes)
    // is the evidence, and the player plays it first. This upload is kept
    // because the canvas replay draws only the base layer (no cursor or
    // overlay layers), so the video is the more faithful picture when it
    // exists; but this browser controls whether it exists at all (a closed tab
    // loses it), so the player labels it "Browser recording (uploaded by the
    // session's own browser)".
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const recordedChunksRef = useRef<Blob[]>([]);
    const [isRecording, setIsRecording] = useState(false);
    const sessionIdRef = useRef<string>(id || '');

    // Parse Guacamole instruction(s) from a message
    const parseInstruction = useCallback((data: string): { opcode: string; args: string[] }[] => {
        const results: { opcode: string; args: string[] }[] = [];
        let pos = 0;
        while (pos < data.length) {
            const elements: string[] = [];
            let terminated = false;
            while (pos < data.length) {
                const dotPos = data.indexOf('.', pos);
                if (dotPos === -1) break;
                const len = parseInt(data.substring(pos, dotPos));
                if (isNaN(len)) break;
                const start = dotPos + 1;
                const value = data.substring(start, start + len);
                elements.push(value);
                pos = start + len;
                if (pos < data.length) {
                    const sep = data[pos];
                    pos++;
                    if (sep === ';') { terminated = true; break; }
                    if (sep !== ',') break;
                }
            }
            if (terminated && elements.length > 0) {
                results.push({ opcode: elements[0], args: elements.slice(1) });
            } else {
                break;
            }
        }
        return results;
    }, []);

    // Get or create a layer
    const getLayer = useCallback((index: number): LayerState => {
        if (!layersRef.current.has(index)) {
            const canvas = document.createElement('canvas');
            canvas.width = 1920;
            canvas.height = 1080;
            layersRef.current.set(index, { canvas, x: 0, y: 0, parent: 0 });
        }
        return layersRef.current.get(index)!;
    }, []);

    // Get canvas + context for a layer (layer 0 = main display canvas)
    const getLayerCanvas = useCallback((index: number): HTMLCanvasElement => {
        if (index === 0 && canvasRef.current) return canvasRef.current;
        return getLayer(index).canvas;
    }, [getLayer]);

    const getLayerCtx = useCallback((index: number): CanvasRenderingContext2D | null => {
        return getLayerCanvas(index).getContext('2d');
    }, [getLayerCanvas]);

    // Handle a single Guacamole instruction
    const handleInstruction = useCallback((opcode: string, args: string[]) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        switch (opcode) {
            // --- Display sizing ---
            case 'size': {
                const layerIdx = parseInt(args[0]);
                const w = parseInt(args[1]);
                const h = parseInt(args[2]);
                if (layerIdx === 0) {
                    // Preserve existing content when resizing main canvas
                    const tempCanvas = document.createElement('canvas');
                    tempCanvas.width = canvas.width;
                    tempCanvas.height = canvas.height;
                    const tempCtx = tempCanvas.getContext('2d');
                    if (tempCtx) tempCtx.drawImage(canvas, 0, 0);
                    canvas.width = w;
                    canvas.height = h;
                    ctx.drawImage(tempCanvas, 0, 0);
                } else {
                    const layer = getLayer(layerIdx);
                    const oldCanvas = layer.canvas;
                    const tempCanvas = document.createElement('canvas');
                    tempCanvas.width = oldCanvas.width;
                    tempCanvas.height = oldCanvas.height;
                    const tempCtx = tempCanvas.getContext('2d');
                    if (tempCtx) tempCtx.drawImage(oldCanvas, 0, 0);
                    oldCanvas.width = w;
                    oldCanvas.height = h;
                    const layerCtx = oldCanvas.getContext('2d');
                    if (layerCtx) layerCtx.drawImage(tempCanvas, 0, 0);
                }
                break;
            }

            // --- Image stream start ---
            case 'img': {
                const streamIdx = args[0];
                const mode = parseInt(args[1]);
                const layer = parseInt(args[2]);
                const mimetype = args[3];
                const x = parseInt(args[4]);
                const y = parseInt(args[5]);
                streamsRef.current.set(streamIdx, {
                    type: 'image', layer, mode, mimetype, x, y, data: ''
                });
                const ws = wsRef.current;
                if (ws && ws.readyState === WebSocket.OPEN) {
                    ws.send(encodeInstruction('ack', streamIdx, 'OK', '0'));
                }
                break;
            }

            // --- Stream data chunk ---
            case 'blob': {
                const streamIdx = args[0];
                const b64data = args[1];
                const stream = streamsRef.current.get(streamIdx);
                if (stream && b64data) {
                    stream.data += b64data;
                }
                // Acknowledge each chunk — CRITICAL for flow control
                const wsBlob = wsRef.current;
                if (wsBlob && wsBlob.readyState === WebSocket.OPEN) {
                    wsBlob.send(encodeInstruction('ack', streamIdx, 'OK', '0'));
                }
                break;
            }

            // --- Stream end ---
            case 'end': {
                const streamIdx = args[0];
                const stream = streamsRef.current.get(streamIdx);
                if (!stream) break;

                if (stream.type === 'image' && stream.data) {
                    const img = new Image();
                    const { layer: layerIdx, mode, x: sx, y: sy, mimetype } = stream;
                    img.onload = () => {
                        const targetCtx = getLayerCtx(layerIdx!);
                        if (targetCtx) {
                            const prevOp = targetCtx.globalCompositeOperation;
                            targetCtx.globalCompositeOperation = getCompositeOp(mode!);
                            targetCtx.drawImage(img, sx!, sy!);
                            targetCtx.globalCompositeOperation = prevOp;
                        }
                    };
                    img.src = `data:${mimetype};base64,${stream.data}`;
                } else if (stream.type === 'clipboard' && stream.data) {
                    // Decode base64 clipboard text
                    try {
                        const text = atob(stream.data);
                        clipboardTextRef.current = text;
                        // copyText (utils/clipboard) also works over plain http. Its
                        // fallback selects an off-screen textarea, which steals focus,
                        // so hand focus back to the remote canvas if it had it.
                        const hadFocus = document.activeElement === canvas;
                        copyText(text).then((ok) => {
                            if (hadFocus) canvas.focus({ preventScroll: true });
                            if (!ok) return; // no clipboard access without a user gesture; nothing to report
                            setClipboardNotice('Copied from remote');
                            setTimeout(() => setClipboardNotice(''), 2000);
                        });
                    } catch (e) {
                        // Base64 decode error
                    }
                }
                streamsRef.current.delete(streamIdx);
                break;
            }

            // --- Inline PNG (legacy) ---
            case 'png': {
                const mode = parseInt(args[0]);
                const layerIdx = parseInt(args[1]);
                const x = parseInt(args[2]);
                const y = parseInt(args[3]);
                const b64data = args[4];
                const img = new Image();
                img.onload = () => {
                    const targetCtx = getLayerCtx(layerIdx);
                    if (targetCtx) {
                        const prevOp = targetCtx.globalCompositeOperation;
                        targetCtx.globalCompositeOperation = getCompositeOp(mode);
                        targetCtx.drawImage(img, x, y);
                        targetCtx.globalCompositeOperation = prevOp;
                    }
                };
                img.src = `data:image/png;base64,${b64data}`;
                break;
            }

            // --- Region copy between layers ---
            case 'copy': {
                const srcLayerIdx = parseInt(args[0]);
                const srcX = parseInt(args[1]);
                const srcY = parseInt(args[2]);
                const srcW = parseInt(args[3]);
                const srcH = parseInt(args[4]);
                const mode = parseInt(args[5]);
                const dstLayerIdx = parseInt(args[6]);
                const dstX = parseInt(args[7]);
                const dstY = parseInt(args[8]);
                const srcCanvas = getLayerCanvas(srcLayerIdx);
                const dstCtx = getLayerCtx(dstLayerIdx);
                if (dstCtx && srcCanvas.width > 0 && srcCanvas.height > 0) {
                    const prevOp = dstCtx.globalCompositeOperation;
                    dstCtx.globalCompositeOperation = getCompositeOp(mode);
                    try {
                        // For same-canvas copies, use intermediate buffer to avoid overlap issues
                        if (srcLayerIdx === dstLayerIdx) {
                            const tmpCanvas = document.createElement('canvas');
                            tmpCanvas.width = srcW;
                            tmpCanvas.height = srcH;
                            const tmpCtx = tmpCanvas.getContext('2d');
                            if (tmpCtx) {
                                tmpCtx.drawImage(srcCanvas, srcX, srcY, srcW, srcH, 0, 0, srcW, srcH);
                                dstCtx.drawImage(tmpCanvas, dstX, dstY);
                            }
                        } else {
                            dstCtx.drawImage(srcCanvas, srcX, srcY, srcW, srcH, dstX, dstY, srcW, srcH);
                        }
                    } catch (e) {
                        // Ignore errors
                    }
                    dstCtx.globalCompositeOperation = prevOp;
                }
                break;
            }

            // --- Path operations ---
            case 'rect': {
                const layerIdx = parseInt(args[0]);
                const x = parseInt(args[1]);
                const y = parseInt(args[2]);
                const w = parseInt(args[3]);
                const h = parseInt(args[4]);
                const targetCtx = getLayerCtx(layerIdx);
                if (targetCtx) {
                    targetCtx.rect(x, y, w, h);
                }
                break;
            }
            case 'start': {
                const layerIdx = parseInt(args[0]);
                const x = parseInt(args[1]);
                const y = parseInt(args[2]);
                const targetCtx = getLayerCtx(layerIdx);
                if (targetCtx) {
                    targetCtx.beginPath();
                    targetCtx.moveTo(x, y);
                }
                break;
            }
            case 'line': {
                const layerIdx = parseInt(args[0]);
                const x = parseInt(args[1]);
                const y = parseInt(args[2]);
                const targetCtx = getLayerCtx(layerIdx);
                if (targetCtx) targetCtx.lineTo(x, y);
                break;
            }
            case 'close': {
                const layerIdx = parseInt(args[0]);
                const targetCtx = getLayerCtx(layerIdx);
                if (targetCtx) targetCtx.closePath();
                break;
            }
            case 'cfill': {
                const mode = parseInt(args[0]);
                const layerIdx = parseInt(args[1]);
                const r = parseInt(args[2]);
                const g = parseInt(args[3]);
                const b = parseInt(args[4]);
                const a = parseInt(args[5]);
                const targetCtx = getLayerCtx(layerIdx);
                if (targetCtx) {
                    const prevOp = targetCtx.globalCompositeOperation;
                    targetCtx.globalCompositeOperation = getCompositeOp(mode);
                    targetCtx.fillStyle = guacColor(r, g, b, a);
                    targetCtx.fill();
                    targetCtx.globalCompositeOperation = prevOp;
                    targetCtx.beginPath(); // Reset path after fill
                }
                break;
            }
            case 'cstroke': {
                const mode = parseInt(args[0]);
                const layerIdx = parseInt(args[1]);
                const cap = parseInt(args[2]); // 0=butt, 1=round, 2=square
                const join = parseInt(args[3]); // 0=bevel, 1=miter, 2=round
                const thickness = parseInt(args[4]);
                const r = parseInt(args[5]);
                const g = parseInt(args[6]);
                const b = parseInt(args[7]);
                const a = parseInt(args[8]);
                const targetCtx = getLayerCtx(layerIdx);
                if (targetCtx) {
                    const prevOp = targetCtx.globalCompositeOperation;
                    targetCtx.globalCompositeOperation = getCompositeOp(mode);
                    targetCtx.lineCap = ['butt', 'round', 'square'][cap] as CanvasLineCap;
                    targetCtx.lineJoin = ['bevel', 'miter', 'round'][join] as CanvasLineJoin;
                    targetCtx.lineWidth = thickness;
                    targetCtx.strokeStyle = guacColor(r, g, b, a);
                    targetCtx.stroke();
                    targetCtx.globalCompositeOperation = prevOp;
                    targetCtx.beginPath();
                }
                break;
            }

            // --- Cursor rendering ---
            case 'cursor': {
                const hotspotX = parseInt(args[0]);
                const hotspotY = parseInt(args[1]);
                const srcLayerIdx = parseInt(args[2]);
                const srcX = parseInt(args[3]);
                const srcY = parseInt(args[4]);
                const srcW = parseInt(args[5]);
                const srcH = parseInt(args[6]);
                cursorHotspotRef.current = { x: hotspotX, y: hotspotY };
                // Create cursor image from source layer
                const srcCanvas = getLayerCanvas(srcLayerIdx);
                if (srcW > 0 && srcH > 0) {
                    const cursorCanvas = document.createElement('canvas');
                    cursorCanvas.width = srcW;
                    cursorCanvas.height = srcH;
                    const cursorCtx = cursorCanvas.getContext('2d');
                    if (cursorCtx) {
                        cursorCtx.drawImage(srcCanvas, srcX, srcY, srcW, srcH, 0, 0, srcW, srcH);
                        cursorCanvasRef.current = cursorCanvas;
                        // Set CSS cursor from canvas data
                        try {
                            const dataURL = cursorCanvas.toDataURL('image/png');
                            if (canvas) {
                                canvas.style.cursor = `url(${dataURL}) ${hotspotX} ${hotspotY}, default`;
                            }
                        } catch (e) {
                            canvas.style.cursor = 'default';
                        }
                    }
                }
                break;
            }

            // --- Clipboard from remote ---
            case 'clipboard': {
                const streamIdx = args[0];
                const mimetype = args[1];
                streamsRef.current.set(streamIdx, {
                    type: 'clipboard', mimetype, data: ''
                });
                const ws = wsRef.current;
                if (ws && ws.readyState === WebSocket.OPEN) {
                    ws.send(encodeInstruction('ack', streamIdx, 'OK', '0'));
                }
                break;
            }

            // --- Layer operations ---
            case 'move': {
                const layerIdx = parseInt(args[0]);
                const parentIdx = parseInt(args[1]);
                const x = parseInt(args[2]);
                const y = parseInt(args[3]);
                // const z = parseInt(args[4]); // z-order, not used for off-screen layers
                if (layerIdx !== 0) {
                    const layer = getLayer(layerIdx);
                    layer.x = x;
                    layer.y = y;
                    layer.parent = parentIdx;
                }
                break;
            }
            case 'shade': {
                // shade,layer,opacity — set layer opacity
                // We don't render layers composited, so skip for now
                break;
            }
            case 'dispose': {
                const layerIdx = parseInt(args[0]);
                layersRef.current.delete(layerIdx);
                break;
            }
            case 'reset': {
                const layerIdx = parseInt(args[0]);
                const targetCtx = getLayerCtx(layerIdx);
                if (targetCtx) {
                    targetCtx.setTransform(1, 0, 0, 1, 0, 0);
                    targetCtx.globalCompositeOperation = 'source-over';
                    targetCtx.beginPath();
                }
                break;
            }
            case 'push': {
                const layerIdx = parseInt(args[0]);
                const targetCtx = getLayerCtx(layerIdx);
                if (targetCtx) targetCtx.save();
                break;
            }
            case 'pop': {
                const layerIdx = parseInt(args[0]);
                const targetCtx = getLayerCtx(layerIdx);
                if (targetCtx) targetCtx.restore();
                break;
            }

            // --- Frame sync ---
            case 'sync': {
                const timestamp = args[0];
                const ws = wsRef.current;
                if (ws && ws.readyState === WebSocket.OPEN) {
                    ws.send(encodeInstruction('sync', timestamp));
                }
                break;
            }

            // --- Connection state ---
            case 'ready':
                setState('connected');
                // Put the keyboard on the remote once it is live; the release combo
                // (or a click outside) gives it back to the page.
                canvas.focus({ preventScroll: true });
                break;
            case 'error': {
                const msg = args[0] || 'Unknown error';
                console.error('Guacamole error:', msg, 'status:', args[1]);
                setError(msg);
                setState('error');
                break;
            }
            case 'disconnect':
            case 'nop':
            case 'ack':
            case 'mouse':
            case 'set':
            case 'name':
            case 'pipe':
            case 'nest':
            case 'msg':
            case 'required':
                break;
        }
    }, [getLayer, getLayerCanvas, getLayerCtx]);

    // Send local clipboard to remote
    const sendClipboard = useCallback((text: string) => {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) return;

        const streamIdx = nextStreamIdRef.current++;
        ws.send(encodeInstruction('clipboard', streamIdx.toString(), 'text/plain'));
        // Send data in base64 chunks
        const b64 = btoa(text);
        const chunkSize = 4096;
        for (let i = 0; i < b64.length; i += chunkSize) {
            ws.send(encodeInstruction('blob', streamIdx.toString(), b64.substring(i, i + chunkSize)));
        }
        ws.send(encodeInstruction('end', streamIdx.toString()));
    }, []);

    // Resolve credentials once, and scrub them from the address bar (F11).
    // The password arrives over postMessage from the opener tab (?handoff=<nonce>,
    // see lib/rdpHandoff). A legacy ?password= link still works once, but is
    // replaced out of the current history entry before anything renders it. The
    // promise lives in a ref so StrictMode's effect re-run reuses the single-use
    // hand-off instead of asking the opener a second time.
    useEffect(() => {
        if (!credsPromiseRef.current) {
            const { search, password: legacyPassword, handoff, changed } = splitSensitiveParams(location.search);
            const username = new URLSearchParams(location.search).get('username') || '';
            if (changed) {
                navigate({ pathname: location.pathname, search, hash: location.hash }, { replace: true, state: location.state });
            }
            credsPromiseRef.current = (handoff ? takeRdpPassword(handoff) : Promise.resolve(null))
                .then(pw => ({ username, password: pw ?? legacyPassword }));
        }
        let cancelled = false;
        credsPromiseRef.current.then(c => { if (!cancelled) setCreds(c); });
        return () => { cancelled = true; };
        // Once per mount: re-running on location would re-read the scrubbed URL.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // WebSocket connection
    useEffect(() => {
        if (!id || !creds) return;

        const { username, password } = creds;
        // Size the remote desktop to the display area actually available, not the
        // window: the page renders inside the app shell (header + padding).
        const area = displayContainerRef.current;
        const width = Math.floor(area?.clientWidth || window.innerWidth);
        const height = Math.floor(area?.clientHeight || window.innerHeight - 56);

        const token = getCookie('auth_token');
        const wsBase = getSocketUrl();
        const tenant = getTenant();
        // TODO(F11, backend): the password still rides in the WS upgrade query,
        // because /api/v1/desktop/connect/:id reads it from there today, so it can
        // still reach proxy/access logs of the upgrade request (never browser
        // history: this URL is not navigated). To finish F11 the server must accept
        // credentials in the first WS message (or a short-lived ticket minted by a
        // POST) and this param must be dropped.
        const params = new URLSearchParams({
            width: width.toString(),
            height: height.toString(),
            ...(username && { username }),
            ...(password && { password }),
            ...(token && { token }),
            ...(tenant && { tenant_id: tenant }),
        });

        const wsUrl = `${wsBase}/api/v1/desktop/connect/${id}?${params.toString()}`;

        // Set initial canvas size
        if (canvasRef.current) {
            canvasRef.current.width = width;
            canvasRef.current.height = height;
        }

        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
            console.log('Desktop WebSocket connected');

            // Start the browser recording (secondary, see mediaRecorderRef above).
            const cvs = canvasRef.current;
            if (cvs) {
                try {
                    const stream = cvs.captureStream(5); // 5 FPS
                    const mimeType = MediaRecorder.isTypeSupported('video/webm; codecs=vp9')
                        ? 'video/webm; codecs=vp9'
                        : MediaRecorder.isTypeSupported('video/webm; codecs=vp8')
                            ? 'video/webm; codecs=vp8'
                            : 'video/webm';
                    const recorder = new MediaRecorder(stream, {
                        mimeType,
                        videoBitsPerSecond: 1000000, // 1 Mbps
                    });
                    recordedChunksRef.current = [];
                    recorder.ondataavailable = (event) => {
                        if (event.data && event.data.size > 0) {
                            recordedChunksRef.current.push(event.data);
                        }
                    };
                    recorder.start(2000); // Collect chunks every 2 seconds
                    mediaRecorderRef.current = recorder;
                    setIsRecording(true);
                    console.log('Canvas recording started:', mimeType);
                } catch (err) {
                    console.warn('Canvas recording not available:', err);
                }
            }
        };

        ws.onmessage = (event) => {
            const instructions = parseInstruction(event.data);
            for (const { opcode, args } of instructions) {
                handleInstruction(opcode, args);
            }
        };

        ws.onclose = (event) => {
            console.log('Desktop WebSocket closed:', event.code, event.reason);
            setState(prev => prev === 'error' ? 'error' : 'disconnected');

            // Stop the browser recording and upload it (secondary; the server
            // record does not depend on this upload succeeding).
            const recorder = mediaRecorderRef.current;
            if (recorder && recorder.state !== 'inactive') {
                recorder.onstop = async () => {
                    setIsRecording(false);
                    const chunks = recordedChunksRef.current;
                    if (chunks.length === 0) {
                        console.log('No recording data collected');
                        return;
                    }
                    const blob = new Blob(chunks, { type: recorder.mimeType || 'video/webm' });
                    console.log('Recording complete:', blob.size, 'bytes');

                    // Upload to server
                    const sid = sessionIdRef.current;
                    if (sid) {
                        try {
                            console.log('Uploading recording for session:', sid);
                            await sessionsApi.uploadRecordingVideo(sid, blob);
                            console.log('Recording uploaded successfully!');
                        } catch (err) {
                            console.error('Upload failed:', err);
                            toast.error('Failed to upload the session recording');
                        }
                    }
                };
                recorder.stop();
            }
        };

        ws.onerror = () => {
            setError('WebSocket connection failed');
            setState('error');
        };

        // Mouse handling
        const cvs = canvasRef.current;
        if (cvs) {
            const sendMouse = (e: MouseEvent) => {
                const rect = cvs.getBoundingClientRect();
                const scaleX = cvs.width / rect.width;
                const scaleY = cvs.height / rect.height;
                const x = Math.floor((e.clientX - rect.left) * scaleX);
                const y = Math.floor((e.clientY - rect.top) * scaleY);
                let buttons = 0;
                if (e.buttons & 1) buttons |= 1;   // left
                if (e.buttons & 4) buttons |= 2;   // middle
                if (e.buttons & 2) buttons |= 4;   // right
                if (ws.readyState === WebSocket.OPEN) {
                    ws.send(encodeInstruction('mouse', x.toString(), y.toString(), buttons.toString()));
                }
            };
            // Scroll wheel support
            const handleWheel = (e: WheelEvent) => {
                e.preventDefault();
                const rect = cvs.getBoundingClientRect();
                const scaleX = cvs.width / rect.width;
                const scaleY = cvs.height / rect.height;
                const x = Math.floor((e.clientX - rect.left) * scaleX);
                const y = Math.floor((e.clientY - rect.top) * scaleY);
                // Scroll up = button 8, scroll down = button 16
                const scrollButton = e.deltaY < 0 ? 8 : 16;
                if (ws.readyState === WebSocket.OPEN) {
                    ws.send(encodeInstruction('mouse', x.toString(), y.toString(), scrollButton.toString()));
                    // Release scroll button
                    ws.send(encodeInstruction('mouse', x.toString(), y.toString(), '0'));
                }
            };
            cvs.addEventListener('mousemove', sendMouse);
            // preventDefault stops text selection but also the default focus, so
            // focus explicitly: clicking the desktop is how keyboard capture starts.
            cvs.addEventListener('mousedown', (e) => { e.preventDefault(); cvs.focus({ preventScroll: true }); sendMouse(e); });
            cvs.addEventListener('mouseup', sendMouse);
            cvs.addEventListener('contextmenu', (e) => e.preventDefault());
            cvs.addEventListener('wheel', handleWheel, { passive: false });
        }

        // Keyboard handling (F42). Listeners sit on the canvas, so keys reach the
        // remote only while the canvas has focus; with focus anywhere else Tab,
        // Enter and Escape behave normally for the page (toolbar, nav).
        const pressed = new Set<number>();
        const releaseAll = () => {
            if (ws.readyState === WebSocket.OPEN) {
                pressed.forEach(k => ws.send(encodeInstruction('key', k.toString(), '0')));
            }
            pressed.clear();
        };
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'F11' || e.key === 'F12') return;
            e.preventDefault();
            if (isReleaseCombo(e)) {
                releaseAll();
                (releaseTargetRef.current ?? cvs)?.focus();
                return;
            }
            const keysym = keyToKeysym(e);
            if (keysym && ws.readyState === WebSocket.OPEN) {
                pressed.add(keysym);
                ws.send(encodeInstruction('key', keysym.toString(), '1'));
            }
        };
        const handleKeyUp = (e: KeyboardEvent) => {
            if (e.key === 'F11' || e.key === 'F12') return;
            e.preventDefault();
            const keysym = keyToKeysym(e);
            if (keysym && ws.readyState === WebSocket.OPEN) {
                pressed.delete(keysym);
                ws.send(encodeInstruction('key', keysym.toString(), '0'));
            }
        };
        // Losing focus mid-chord would leave keys held down on the remote.
        const handleBlur = () => { releaseAll(); setCanvasFocused(false); };
        const handleFocus = () => setCanvasFocused(true);
        cvs?.addEventListener('keydown', handleKeyDown);
        cvs?.addEventListener('keyup', handleKeyUp);
        cvs?.addEventListener('blur', handleBlur);
        cvs?.addEventListener('focus', handleFocus);

        // Paste handler — send clipboard to remote, only while the canvas has
        // focus (a paste into anything else on the page stays on the page).
        const handlePaste = (e: ClipboardEvent) => {
            if (!cvs || document.activeElement !== cvs) return;
            const text = e.clipboardData?.getData('text/plain');
            if (text) {
                e.preventDefault();
                sendClipboard(text);
            }
        };
        document.addEventListener('paste', handlePaste);

        return () => {
            cvs?.removeEventListener('keydown', handleKeyDown);
            cvs?.removeEventListener('keyup', handleKeyUp);
            cvs?.removeEventListener('blur', handleBlur);
            cvs?.removeEventListener('focus', handleFocus);
            document.removeEventListener('paste', handlePaste);
            // Stop recorder if still active
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.stop();
            }
            ws.close();
        };
    }, [id, creds, parseInstruction, handleInstruction, sendClipboard]);

    // Fullscreen toggle
    const toggleFullscreen = () => {
        if (!containerRef.current) return;
        if (!document.fullscreenElement) {
            containerRef.current.requestFullscreen();
            setIsFullscreen(true);
        } else {
            document.exitFullscreen();
            setIsFullscreen(false);
        }
    };

    const handleDisconnect = () => {
        if (wsRef.current) wsRef.current.close();
        navigate('/desktop-resources');
    };

    const status =
        state === 'connected' ? { value: 'connected', label: 'Connected' } :
            state === 'connecting' ? { value: 'running', label: 'Connecting...' } :
                state === 'error' ? { value: 'failed', label: 'Error' } :
                    { value: 'disconnected', label: 'Disconnected' };

    return (
        // h-screen: the route renders outside the app shell, like the SSH and K8s
        // terminals (App.tsx), so the session fills the viewport without
        // overflowing it (F42).
        <div ref={containerRef} className="zta-term-chrome flex h-screen min-h-[24rem] flex-col bg-[var(--d2d-bg-primary)]">
            {/* Toolbar */}
            <div className="z-10 flex h-14 shrink-0 items-center justify-between border-b border-[var(--d2d-border)] bg-[var(--d2d-bg-secondary)] px-4">
                <div className="flex items-center gap-3">
                    <Button ref={releaseTargetRef} variant="ghost" size="sm" leadingIcon={<ArrowLeft className="h-4 w-4" />} onClick={handleDisconnect}>
                        Back
                    </Button>
                    <div aria-hidden="true" className="h-5 w-px bg-[var(--d2d-border)]" />
                    <div className="flex items-center gap-2">
                        <Monitor aria-hidden="true" className="h-4 w-4 text-[var(--brand)]" />
                        <h1 className="text-sm font-medium text-[var(--d2d-text-primary)]"><span className="sr-only">Desktop session: </span>{resourceName}</h1>
                        <OutcomeBadge value={status.value} shape="chip">{status.label}</OutcomeBadge>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {state === 'connected' && (
                        <span aria-live="polite" className="hidden text-xs text-[var(--d2d-text-muted)] md:inline">
                            {canvasFocused ? `Keyboard goes to the desktop · ${RELEASE_HINT} to release` : 'Click the desktop to type into it'}
                        </span>
                    )}
                    {clipboardNotice && (
                        <Chip size="sm" variant="neutral" icon={<Clipboard className="h-3 w-3" />} className="animate-pulse">
                            {clipboardNotice}
                        </Chip>
                    )}
                    {isRecording && (
                        <RecordingIndicator />
                    )}
                    <IconButton
                        aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                        size="sm"
                        icon={isFullscreen ? <Minimize2 /> : <Maximize2 />}
                        onClick={toggleFullscreen}
                    />
                    <Button variant="danger" size="sm" leadingIcon={<Power className="h-3.5 w-3.5" />} onClick={handleDisconnect}>
                        Disconnect
                    </Button>
                </div>
            </div>

            {/* Display area */}
            <div
                ref={displayContainerRef}
                className="relative flex flex-1 items-center justify-center overflow-hidden"
                style={{ backgroundColor: canvasBackground.desktop }}
            >
                {state === 'connecting' && (
                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-[var(--d2d-bg-primary)]/80">
                        {/* ds Spinner, not LoadingRegion: this is a wait over the live RDP stream canvas, where skeleton lines would imply content that is not coming; the h2 below carries the text. */}
                        <Spinner size={48} className="mb-4 text-[var(--brand)]" />
                        <h2 className="mb-1 text-lg font-medium text-[var(--d2d-text-primary)]">Connecting to Desktop...</h2>
                        <p className="text-sm text-[var(--d2d-text-muted)]">Establishing RDP session</p>
                    </div>
                )}
                {state === 'error' && (
                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-[var(--d2d-bg-primary)]/80">
                        <AlertCircle aria-hidden="true" className="mb-4 h-12 w-12 text-[var(--danger-text)]" />
                        <h2 className="mb-1 text-lg font-medium text-[var(--d2d-text-primary)]">Connection Failed</h2>
                        <p className="mb-4 text-sm text-[var(--danger-text)]">{error}</p>
                        <div className="flex gap-3">
                            <Button onClick={() => window.location.reload()}>Retry</Button>
                            <Button variant="secondary" onClick={handleDisconnect}>Go Back</Button>
                        </div>
                    </div>
                )}
                {state === 'disconnected' && (
                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-[var(--d2d-bg-primary)]/80">
                        <Monitor aria-hidden="true" className="mb-4 h-12 w-12 text-[var(--d2d-text-muted)]" />
                        <h2 className="mb-1 text-lg font-medium text-[var(--d2d-text-primary)]">Session Ended</h2>
                        <p className="mb-4 text-sm text-[var(--d2d-text-muted)]">The RDP session has been disconnected.</p>
                        <Button onClick={handleDisconnect}>Go Back</Button>
                    </div>
                )}
                <canvas
                    ref={canvasRef}
                    tabIndex={0}
                    role="application"
                    aria-label={`Remote desktop ${resourceName}. While focused, all keys go to the remote computer; press ${RELEASE_HINT} to release the keyboard.`}
                    className="max-h-full max-w-full object-contain outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-ring)]"
                />
            </div>
        </div>
    );
}
